import { useEffect, useRef } from 'react'

/**
 * Custom hook to defer function execution until after component state updates.
 *
 * This prevents React warnings about calling setState during render cycles
 * by ensuring function calls are executed in the next render cycle via useEffect.
 *
 * @param fn - The function to execute
 * @returns A trigger function that schedules the function to be called
 *
 * @example
 * ```tsx
 * const triggerCallback = useDeferredCall(onFiltersChange)
 *
 * const handleChange = () => {
 *   setFilters(newFilters)
 *   triggerCallback(newFilters) // Will be called after state update
 * }
 * ```
 */
const useDeferredCall = <T extends any[]>(fn?: (...args: T) => void) => {
  const shouldCall = useRef(false)
  const args = useRef<T>()

  useEffect(() => {
    if (shouldCall.current && fn) {
      fn(...args.current!)
      shouldCall.current = false
    }
  })

  return (...callArgs: T) => {
    args.current = callArgs
    shouldCall.current = true
  }
}

export default useDeferredCall
